import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Calendar, 
  Coins, 
  TrendingUp, 
  Settings, 
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  ArrowDown,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import paymentService from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';

const BillingDashboard: React.FC = () => {
  const { user } = useAuth();
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'warning' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const { data: billingSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => paymentService.getBillingSummary(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const { data: usageStats, isLoading: usageLoading } = useQuery({
    queryKey: ['usage-stats'],
    queryFn: () => paymentService.getUsageStats(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  const { data: tiers } = useQuery({
    queryKey: ['tiers'],
    queryFn: async () => {
      const response = await fetch('/api/v1/tiers/public?active_only=true');
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: scheduledChange } = useQuery({
    queryKey: ['scheduled-tier-change'],
    queryFn: () => paymentService.getScheduledTierChange(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  const handleOpenCustomerPortal = async () => {
    console.log('🔄 Opening customer portal...');
    try {
      await paymentService.openCustomerPortal();
      console.log('✅ Customer portal opened successfully');
    } catch (error) {
      console.error('❌ Failed to open customer portal:', error);
      setNotification({ 
        type: 'error', 
        message: 'Unable to open billing portal. Please try again or contact support.' 
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleCancelScheduledChange = async () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Scheduled Change',
      message: 'Are you sure you want to cancel the scheduled tier change? You will continue on your current plan.',
      confirmText: 'Cancel Change',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        
        try {
          const result = await paymentService.cancelScheduledTierChange();
          console.log('✅ Scheduled change canceled:', result);
          
          setNotification({ 
            type: 'success', 
            message: result.message || 'Scheduled tier change has been canceled.' 
          });
          setTimeout(() => window.location.reload(), 2000);
        } catch (error: any) {
          console.error('❌ Failed to cancel scheduled change:', error);
          
          setNotification({ 
            type: 'error', 
            message: `Unable to cancel scheduled change: ${error.response?.data?.detail || error.message}` 
          });
          setTimeout(() => setNotification(null), 5000);
        }
      }
    });
  };

  const handleDowngradeTier = (targetTierId: string, targetTierName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: `Downgrade to ${targetTierName}`,
      message: `Are you sure you want to downgrade to the ${targetTierName} plan? The change will take effect at the end of your current billing period.`,
      confirmText: 'Downgrade',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        console.log(`🔄 Downgrading to ${targetTierName}...`);
        
        try {
          const result = await paymentService.downgradeTier(targetTierId);
          console.log('✅ Downgrade successful:', result);
          setNotification({ 
            type: 'success', 
            message: result.message || `Successfully downgraded to ${targetTierName}` 
          });
          setTimeout(() => window.location.reload(), 2000);
        } catch (error: any) {
          console.error('❌ Failed to downgrade:', error);
          setNotification({ 
            type: 'error', 
            message: `Unable to downgrade: ${error.response?.data?.detail || error.message}. Please try again or contact support.` 
          });
          setTimeout(() => setNotification(null), 5000);
        }
      }
    });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  if (summaryLoading || usageLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Notification Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 right-4 z-50 max-w-md"
          >
            <div className={`rounded-lg shadow-lg p-4 flex items-start space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              {notification.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' 
                    ? 'text-green-900 dark:text-green-100' 
                    : 'text-red-900 dark:text-red-100'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className={`flex-shrink-0 ${
                  notification.type === 'success' 
                    ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300' 
                    : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  confirmDialog.type === 'danger' 
                    ? 'bg-red-100 dark:bg-red-900/30' 
                    : 'bg-orange-100 dark:bg-orange-900/30'
                }`}>
                  <AlertCircle className={`w-6 h-6 ${
                    confirmDialog.type === 'danger' 
                      ? 'text-red-600 dark:text-red-400' 
                      : 'text-orange-600 dark:text-orange-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-black dark:text-white mb-2">
                    {confirmDialog.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                  className="px-4 py-2"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDialog.onConfirm}
                  className={`px-4 py-2 ${
                    confirmDialog.type === 'danger'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {confirmDialog.confirmText || 'Confirm'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-black dark:text-white">Billing & Usage</h1>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleOpenCustomerPortal}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span>Manage Billing</span>
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Scheduled Tier Change Banner */}
      {scheduledChange && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={
            scheduledChange.reason === 'subscription_canceled'
              ? "bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border-2 border-orange-300 dark:border-orange-700 rounded-xl p-6 shadow-sm"
              : "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-6 shadow-sm"
          }
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {scheduledChange.reason === 'subscription_canceled' ? (
                <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              ) : (
                <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold mb-1 ${
                scheduledChange.reason === 'subscription_canceled'
                  ? 'text-orange-900 dark:text-orange-100'
                  : 'text-blue-900 dark:text-blue-100'
              }`}>
                {scheduledChange.reason === 'subscription_canceled' 
                  ? '⚠️ Subscription Canceled' 
                  : '📅 Scheduled Plan Change'}
              </h3>
              <p className={
                scheduledChange.reason === 'subscription_canceled'
                  ? 'text-orange-800 dark:text-orange-200 mb-2'
                  : 'text-blue-800 dark:text-blue-200 mb-2'
              }>
                Your plan will change from{' '}
                <span className="font-semibold">{scheduledChange.current_tier_name}</span>
                {' '}to{' '}
                <span className="font-semibold">{scheduledChange.scheduled_tier_name}</span>
                {' '}on{' '}
                <span className="font-semibold">
                  {new Date(scheduledChange.effective_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                {' '}({scheduledChange.days_until_change} days from now).
              </p>
              <p className={`text-sm mb-4 ${
                scheduledChange.reason === 'subscription_canceled'
                  ? 'text-orange-700 dark:text-orange-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                {scheduledChange.reason === 'subscription_canceled' 
                  ? `Your subscription was canceled. You'll keep access to all ${scheduledChange.current_tier_name} features until then. You can reactivate your subscription anytime before the change takes effect.`
                  : `You'll continue to have access to all ${scheduledChange.current_tier_name} features until then.`
                }
              </p>
              {scheduledChange.reason === 'subscription_canceled' ? (
                <div className="flex gap-3">
                  <Button
                    onClick={() => window.open(billingSummary?.portal_url, '_blank')}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Reactivate Subscription
                  </Button>
                  <Button
                    onClick={handleCancelScheduledChange}
                    variant="outline"
                    className="border-orange-600 text-orange-600 hover:bg-orange-50 dark:border-orange-400 dark:text-orange-400 dark:hover:bg-orange-900/30"
                  >
                    Cancel Scheduled Change
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleCancelScheduledChange}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/30"
                >
                  Cancel Scheduled Change
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Cancellation Notice Banner - Now handled by Scheduled Tier Change Banner above */}

      {/* Current Plan */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 border border-indigo-200 dark:border-indigo-700 rounded-xl p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-1">Current Plan</h2>
            <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
              {user?.tier?.display_name || 'Free Plan'}
            </p>
            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
              {user?.credits?.toFixed(0) || '0'} credits remaining
            </p>
          </div>
          <div className="p-3 bg-indigo-100 dark:bg-indigo-800 rounded-lg">
            <CreditCard className="w-8 h-8 text-indigo-600 dark:text-indigo-300" />
          </div>
        </div>

        {/* Subscription Details */}
        {billingSummary?.current_subscription && (
          <div className="pt-4 border-t border-indigo-200 dark:border-indigo-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400">Status</p>
                <p className="font-semibold text-indigo-900 dark:text-indigo-100 capitalize">
                  {billingSummary.current_subscription.status}
                </p>
              </div>
              
              {billingSummary.current_subscription.current_period_end && (
                <div>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400">Next Billing Date</p>
                  <p className="font-semibold text-indigo-900 dark:text-indigo-100">
                    {formatDate(billingSummary.current_subscription.current_period_end)}
                  </p>
                </div>
              )}
              
              {billingSummary.current_subscription.days_until_renewal && (
                <div>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400">Days Until Renewal</p>
                  <p className="font-semibold text-indigo-900 dark:text-indigo-100">
                    {billingSummary.current_subscription.days_until_renewal} days
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Current Credits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Current Credits</p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                {user?.credits?.toFixed(0) || '0'}
              </p>
            </div>
            <Coins className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </motion.div>

        {/* Total Spent */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-700 rounded-xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">Total Spent</p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                €{billingSummary?.total_spent?.toFixed(2) || '0.00'}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        </motion.div>

        {/* Credits Purchased */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border border-purple-200 dark:border-purple-700 rounded-xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Credits Purchased</p>
              <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                {billingSummary?.credits_purchased?.toFixed(0) || '0'}
              </p>
            </div>
            <Download className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
        </motion.div>
      </div>

      {/* Current Subscription section merged into Current Plan card above */}

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6"
      >
        <h2 className="text-xl font-semibold text-black dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-black dark:text-gray-300" />
          Recent Transactions
        </h2>

        {billingSummary?.recent_transactions && billingSummary.recent_transactions.length > 0 ? (
          <div className="space-y-3">
            {billingSummary.recent_transactions
              .slice(0, 5)
              .map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getStatusIcon(transaction.status)}
                  <div>
                    <p className="font-medium text-black dark:text-white">
                      {transaction.description || `${transaction.payment_type} payment`}
                    </p>
                    <p className="text-sm text-black dark:text-gray-300">
                      {formatDate(transaction.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-black dark:text-white">
                    €{(transaction.amount_dollars || transaction.amount_cents / 100 || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-black dark:text-gray-300 capitalize">
                    {transaction.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-black dark:text-gray-400 text-center py-8">
            No transactions found.
          </p>
        )}
      </motion.div>

      {/* Feature Usage section removed */}
    </div>
  );
};

export default BillingDashboard;